import { useQuery } from "@apollo/client";
import { GridLegacy as Grid, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { gql } from "../../../../generated-graphql";
import { TierIiReportKind } from "../../../../generated-graphql/graphql";
import { UnverifiedTable } from "./UnverifiedTable";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { ChemicalFacilitiesDoughnutChart } from "./DoughnutChart/chemicalFacilitiesDoughnutChart";
import { KeyMetrics } from "./KeyMetrics";
import { useOmnisearchInput } from "../../../../hooks/useOmnisearchDatagrid/useOmnisearchInput";

const CHEMICAL_FACILITIES_SUMMARY = gql(`
  query ChemicalFacilitiesSummary($year: Int!, $reportKinds: [TierIIReportKind!]) {
    reportingSeasonSummary(year: $year, reportKinds: $reportKinds) {
      year
      noReportsTotal
      facilitiesTotal
      reportsInFilingComplete
      reportsInAwaitingVerification
      reportsInNotReporting
      reportsInProcessing
      reportsUnverified
      reportsTotal
      workflowsCountHuman
      workflowsCountError
      workflowsCountWaitForAll
      stateSummaries {
        id
        name
        unverifiedCount
        reportCount
      }
      organizationSummaries {
        id
        name
        unverifiedCount
        reportCount
      }
    }
  }
`);

export function Summary() {
  const { getOmnisearchValue } = useOmnisearchInput({
    isURLDriven: true,
  });

  function isValTierIiReportKind(val: any): val is TierIiReportKind {
    const validReportKinds = Object.values(TierIiReportKind);
    if (validReportKinds.includes(val)) return true;
    return false;
  }

  const year = useMemo(() => {
    const reportingYear = getOmnisearchValue("reportingYear");
    return reportingYear
      ? parseInt(reportingYear, 10)
      : currentTierIIReportingYear;
  }, [getOmnisearchValue]);

  const reportKinds = useMemo(() => {
    const reportKind = getOmnisearchValue("reportKind");
    if (!reportKind) return null;

    const reportKinds = reportKind.split("|");

    const validReportKinds = [];

    for (const reportKind of reportKinds) {
      if (isValTierIiReportKind(reportKind)) {
        validReportKinds.push(reportKind);
      }
    }

    return validReportKinds;
  }, [getOmnisearchValue]);

  const { data, loading } = useQuery(CHEMICAL_FACILITIES_SUMMARY, {
    fetchPolicy: "network-only",
    variables: {
      year,
      reportKinds,
    },
  });

  return (
    <Grid container spacing={3}>
      <Grid item lg={6} xs={12}>
        <ChemicalFacilitiesDoughnutChart
          data={data?.reportingSeasonSummary}
          loading={loading}
        />
      </Grid>
      <Grid item md={12} lg={6}>
        <KeyMetrics data={data?.reportingSeasonSummary} loading={loading} />
      </Grid>
      <Grid item md={12} lg={6} sx={{ height: "100%" }}>
        <UnverifiedTable
          type="State"
          title="Reports by State"
          data={data?.reportingSeasonSummary.stateSummaries}
          loading={loading}
          hideRowDividers
        />
      </Grid>
      <Grid item md={12} lg={6} sx={{ height: "100%" }}>
        <UnverifiedTable
          title="Reports by Organization"
          type="Organization"
          data={data?.reportingSeasonSummary.organizationSummaries}
          loading={loading}
          hideRowDividers
        />
      </Grid>
    </Grid>
  );
}
